/**
 * EVL-2 — `seedFromLineage` Author-dialog pre-fill.
 *
 * Given a `RequirementLineage` (EVL-1) + the requirement's
 * `authoredSchema`, walk the schema fields and try to pull a starting
 * value out of the lineage's populated SED slots. Each prefilled
 * value carries metadata so the dialog can render a "Pre-filled from
 * <source>" badge and the save path can stamp `prefilled` vs
 * `user-edited` provenance on the resulting claim.
 *
 * Mapping rule (in order):
 *   1. Explicit override in `REQUIREMENT_FIELD_TO_SLOT_MAP` (per
 *      requirement, per field key).
 *   2. Direct match: a SED slot's `data[field.key]` (case-sensitive).
 *   3. Common-alias match (e.g. `'utilityName'` ↔ `'utilityName'`
 *      across multiple slots).
 *
 * Conservative — never overwrites a user-typed value (the caller's
 * `useState` initializer runs the seed exactly once on dialog mount).
 *
 * Pure projection — no I/O.
 */
import type { AuthoredFieldDef, AuthoredEvidenceFormState } from '../../types/authoredEvidence';
import { makeEmptyForm, inferAuthoredConfidence } from '../../types/authoredEvidence';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { RequirementLineage, SedSlotLineage } from './requirementLineage';
import { extractFieldValue, type FieldPath } from './requirementToFields';
import {
  buildRequirementFieldToSlotMap,
  getFieldAlignment,
  getAlignedFieldPaths,
  resolveAlignedSedField,
} from '../coverage/requirementFieldAlignment';
import { canPopulateFromTier } from '../coverage/populateSourcePolicy';
import { REQUIREMENT_TO_SECTION_MAP } from '../../config/requirementToSection';

/**
 * Per-(requirement, fieldKey) explicit slot path override. Derived from
 * `REQUIREMENT_FIELD_ALIGNMENT` — see requirementFieldAlignment.ts.
 */
export const REQUIREMENT_FIELD_TO_SLOT_MAP: Record<string, Record<string, string>> =
  buildRequirementFieldToSlotMap();

/** Per-prefilled field — what source produced the value. */
export interface PrefilledFieldOrigin {
  /** Human-readable label rendered in the badge (e.g. "WRI Aqueduct"). */
  source: string;
  /** 0..1. */
  confidence: number;
  /** Slot section id the value came from. */
  sectionId: string;
  /** Provenance class for the save-path tag. */
  provenance: SedSlotLineage['provenance'];
}

export interface SeedFromLineageResult {
  form: AuthoredEvidenceFormState;
  prefilledFrom: Record<string, PrefilledFieldOrigin>;
}

/* ── Helpers ──────────────────────────────────────────────────────── */

function readPath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    if (typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/**
 * Coerce a SED-derived value to the schema field's declared type.
 * Returns `undefined` when the coercion would lose data (e.g. a
 * non-numeric string into a `'number'` field) so the dialog falls
 * through to empty rather than rendering a garbled value.
 */
function coerceValue(
  raw: unknown,
  field: AuthoredFieldDef,
): string | number | boolean | undefined {
  if (raw === null || raw === undefined) return undefined;
  switch (field.type) {
    case 'number': {
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw);
        if (Number.isFinite(n)) return n;
      }
      return undefined;
    }
    case 'boolean': {
      if (typeof raw === 'boolean') return raw;
      if (typeof raw === 'string') {
        const lower = raw.trim().toLowerCase();
        if (lower === 'true' || lower === 'yes') return true;
        if (lower === 'false' || lower === 'no') return false;
      }
      return undefined;
    }
    case 'list': {
      if (Array.isArray(raw)) return raw.filter(x => typeof x === 'string').join(', ');
      if (typeof raw === 'string' && raw.trim() !== '') return raw;
      return undefined;
    }
    case 'enum': {
      const s = String(raw).trim();
      if (!s) return undefined;
      const enumValues = field.enumValues ?? [];
      // Case-insensitive match against allowed enum values.
      const hit = enumValues.find(v => v.toLowerCase() === s.toLowerCase());
      return hit ?? undefined;
    }
    case 'date':
    case 'string':
    case 'textarea':
    default: {
      const s = String(raw).trim();
      if (!s) return undefined;
      return s;
    }
  }
}

/**
 * Resolve a SED slot value for a given dotted path
 * (`<sectionId>.<fieldKey>`). Walks `lineage.sources.sedSlots[*]` for
 * the section, then descends into the slot's `providerData`.
 */
function resolveFromLineage(
  lineage: RequirementLineage,
  slotPath: string,
): { value: unknown; slot: SedSlotLineage } | undefined {
  const [sectionId, ...rest] = slotPath.split('.');
  const slot = lineage.sources.sedSlots.find(s => s.sectionId === sectionId);
  if (!slot) return undefined;
  // No subpath -> return the slot's providerData itself (rare).
  if (rest.length === 0) return { value: slot.providerData, slot };
  const value = readPath(slot.providerData, rest.join('.'));
  if (value === undefined || value === null) return undefined;
  return { value, slot };
}

/**
 * Common-alias fall-through: try the schema field's `key` directly
 * against every populated slot's providerData. First non-nullish
 * match wins.
 */
function resolveByAlias(
  lineage: RequirementLineage,
  field: AuthoredFieldDef,
): { value: unknown; slot: SedSlotLineage } | undefined {
  const alignment = getFieldAlignment(lineage.requirementId, field.key);
  if (alignment?.kind === 'doc-only') return undefined;
  for (const slot of lineage.sources.sedSlots) {
    if (!mayUseSlotForField(lineage.requirementId, field.key, slot, 'sed')) continue;
    const direct = readPath(slot.providerData, field.key);
    if (direct !== undefined && direct !== null) return { value: direct, slot };
  }
  return undefined;
}

function slotTierForPopulate(slot: SedSlotLineage): 'doc' | 'sed' | 'dam' | 'research' {
  if (slot.status === 'verified-from-doc' || slot.provenance === 'doc') return 'doc';
  if (slot.status === 'verified-from-dam') return 'dam';
  if (slot.status === 'verified-from-research') return 'research';
  return 'sed';
}

function mayUseSlotForField(
  requirementId: string,
  fieldKey: string,
  slot: SedSlotLineage,
  tier: 'sed' | 'car',
): boolean {
  const slotTier = slotTierForPopulate(slot);
  if (tier === 'car') {
    return canPopulateFromTier(requirementId, fieldKey, 'car');
  }
  // sed path — map slot tier to populate tier
  if (slotTier === 'doc') return canPopulateFromTier(requirementId, fieldKey, 'doc');
  if (slotTier === 'dam') return canPopulateFromTier(requirementId, fieldKey, 'dam');
  if (slotTier === 'research') return canPopulateFromTier(requirementId, fieldKey, 'research');
  return canPopulateFromTier(requirementId, fieldKey, 'sed');
}

function resolveDocOnlyField(
  lineage: RequirementLineage,
  field: AuthoredFieldDef,
): { value: unknown; slot: SedSlotLineage } | undefined {
  for (const slot of lineage.sources.sedSlots) {
    const fromAutoFill = slot.autoFillFields?.[field.key];
    if (fromAutoFill !== undefined && fromAutoFill !== null) {
      if (slot.autoFillProvider === 'user-doc' || slotTierForPopulate(slot) === 'doc') {
        return { value: fromAutoFill, slot };
      }
    }
    if (slotTierForPopulate(slot) !== 'doc') continue;
    const direct = readPath(slot.providerData, field.key);
    if (direct !== undefined && direct !== null) return { value: direct, slot };
  }
  return undefined;
}

function resolveByAutoFillFields(
  lineage: RequirementLineage,
  field: AuthoredFieldDef,
): { value: unknown; slot: SedSlotLineage } | undefined {
  if (!canPopulateFromTier(lineage.requirementId, field.key, 'car')) return undefined;
  for (const slot of lineage.sources.sedSlots) {
    if (!mayUseSlotForField(lineage.requirementId, field.key, slot, 'car')) continue;
    const raw = slot.autoFillFields?.[field.key];
    if (raw === undefined || raw === null) continue;
    return { value: raw, slot };
  }
  return undefined;
}

/**
 * Resolve authored-schema fields directly from the live SED when lineage
 * projection missed them (e.g. section populated but not in sedSlots).
 */
export function seedFromSedDocument(
  requirementId: string,
  sed: SiteEvidenceDocument | undefined,
  schema: readonly AuthoredFieldDef[],
): SeedFromLineageResult {
  const form = makeEmptyForm(schema);
  const prefilledFrom: Record<string, PrefilledFieldOrigin> = {};
  if (!sed || !schema || schema.length === 0) {
    return { form, prefilledFrom };
  }
  const overrideMap = REQUIREMENT_FIELD_TO_SLOT_MAP[requirementId] ?? {};
  for (const field of schema) {
    if (field.key === 'notes') continue;
    const alignment = getFieldAlignment(requirementId, field.key);
    if (alignment?.kind === 'doc-only') {
      // Doc-only keys: only prefill from user-doc autoFillFields on SED.
      const sectionId =
        overrideMap[field.key]?.split('.')[0]
        ?? REQUIREMENT_TO_SECTION_MAP[requirementId]?.[0];
      if (!sectionId) continue;
      const slot = (sed as unknown as Record<string, { provenanceDetail?: { autoFillFields?: Record<string, unknown> } } | undefined>)[sectionId];
      const raw = slot?.provenanceDetail?.autoFillFields?.[field.key];
      if (raw === undefined || raw === null) continue;
      if (!canPopulateFromTier(requirementId, field.key, 'doc')) continue;
      const coerced = coerceValue(raw, field);
      if (coerced === undefined) continue;
      form.fields[field.key] = coerced;
      prefilledFrom[field.key] = {
        source: 'User document',
        confidence: 0.7,
        sectionId,
        provenance: 'doc',
      };
      continue;
    }
    if (!canPopulateFromTier(requirementId, field.key, 'sed')) continue;
    const aligned = resolveAlignedSedField(sed, requirementId, field.key);
    if (aligned) {
      const coerced = coerceValue(aligned.value, field);
      if (coerced !== undefined) {
        form.fields[field.key] = coerced;
        prefilledFrom[field.key] = {
          source: humanizeSedSource(aligned.sectionId),
          confidence: aligned.confidence,
          sectionId: aligned.sectionId,
          provenance: aligned.provenance === 'authored' ? 'authored' : 'provider',
        };
      }
      continue;
    }
    const overridePath = overrideMap[field.key];
    if (!overridePath) continue;
    const resolved = extractFieldValue(sed, overridePath as FieldPath);
    if (resolved.isEmpty) continue;
    const coerced = coerceValue(resolved.value, field);
    if (coerced === undefined) continue;
    form.fields[field.key] = coerced;
    prefilledFrom[field.key] = {
      source: humanizeSedSource(resolved.sectionId),
      confidence: resolved.confidence,
      sectionId: resolved.sectionId,
      provenance: resolved.provenance === 'authored' ? 'authored' : 'provider',
    };
  }
  form.confidence = inferAuthoredConfidence(form, schema);
  return { form, prefilledFrom };
}

function humanizeSedSource(sectionId: string): string {
  return `SED ${sectionId}`;
}


/**
 * Walk `schema.fields[]` and pre-fill each field from the strongest
 * matching SED slot. Returns the seeded form + per-field origin
 * metadata for badge rendering.
 *
 * Empty schema → empty form (the dialog still renders prose-only).
 */
export function seedFromLineage(
  lineage: RequirementLineage,
  schema: readonly AuthoredFieldDef[],
): SeedFromLineageResult {
  const form = makeEmptyForm(schema);
  const prefilledFrom: Record<string, PrefilledFieldOrigin> = {};
  if (!lineage || !schema || schema.length === 0) {
    return { form, prefilledFrom };
  }
  const overrideMap = REQUIREMENT_FIELD_TO_SLOT_MAP[lineage.requirementId] ?? {};
  for (const field of schema) {
    if (field.key === 'notes') continue;
    const alignment = getFieldAlignment(lineage.requirementId, field.key);
    let found: { value: unknown; slot: SedSlotLineage } | undefined;

    if (alignment?.kind === 'doc-only') {
      found = resolveDocOnlyField(lineage, field);
    } else {
      // 1. Explicit alignment paths (primary + fallbacks).
      for (const path of getAlignedFieldPaths(lineage.requirementId, field.key)) {
        found = resolveFromLineage(lineage, path);
        if (found && mayUseSlotForField(lineage.requirementId, field.key, found.slot, 'sed')) {
          break;
        }
        found = undefined;
      }
      // Legacy override map (first path only — kept for transform rows).
      const overridePath = overrideMap[field.key];
      if (!found && overridePath) {
        found = resolveFromLineage(lineage, overridePath);
        if (found && !mayUseSlotForField(lineage.requirementId, field.key, found.slot, 'sed')) {
          found = undefined;
        }
      }
      // 2. Alias fall-through.
      if (!found) {
        found = resolveByAlias(lineage, field);
      }
      // 3. CAR-6 auto-fill payload on the slot (authored-schema keys).
      if (!found) {
        found = resolveByAutoFillFields(lineage, field);
      }
    }
    if (!found) continue;
    const coerced = coerceValue(found.value, field);
    if (coerced === undefined) continue;
    form.fields[field.key] = coerced;
    prefilledFrom[field.key] = {
      source: found.slot.autoFillProvider
        ?? found.slot.providerLabel
        ?? found.slot.providerKey
        ?? 'Provider',
      confidence: found.slot.confidence,
      sectionId: found.slot.sectionId,
      provenance: found.slot.provenance,
    };
  }
  // Re-infer confidence after pre-filling so the dialog defaults to
  // the right band immediately. The user can still override.
  form.confidence = inferAuthoredConfidence(form, schema);
  return { form, prefilledFrom };
}
